//#region imports
import React, { useEffect, useState } from "react";

// styling
import { Colors, Spacing } from "_styles";
import * as TaskColors from "../utils/colors";
import "./job-card.css"
import { mainContainer } from "./job-styles";

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectUserDb } from "_redux/slices/main-screen/user";
import { setPoints, setClicked, setClickedJob, resetFlightContext } from "_redux/slices/utils/menu-context";
import { selectSelectedTask, setSelectedTask, setSelectedTaskList, selectSelectedTasksList, selectSelectedMultiplePAXTask, setSelectedMultiplePAXTask, setSelectedMultiplePAXTaskError } from "_redux/slices/timeline/selected-task";
import { selectFlightListIndexed } from '_redux/slices/timeline/flight-information-list';
import { selectPreBoard, selectRemovePaxNamePrefix, selectThirtyMinLayout } from "_redux/slices/timeline/timeline-control";
import { selectUsedTheme } from '_redux/slices/timeline/theme';

// utils
import { handleNotesText, processName } from "_utils/functions";
import { EmployeeJobCardWidth, JobCard30MinLayoutWidth, JobCardWidth, minute30MinLayoutWidth } from "_utils/timeline-constants";
import { PRMCodes } from "_utils/constants";

// icons
import { PiNotepadFill } from "react-icons/pi";
import { GoDuplicate } from "react-icons/go";

// custom components
import TaskAccept from "./task-accept";
import BreakTaskCard from "./break-task-card";
import JobStatus from "./job-status";
import GroupedPassengerIcon from "./grouped-passenger-icon";
import { MdConnectingAirports } from "react-icons/md";


//#endregion


const JobCard = (props) => {

    //#region redux
    const dispatch = useDispatch();
    const userDb = useSelector(selectUserDb);

    // selected task redux
    const selectedTask = useSelector(selectSelectedTask);                           // holds selected task
    const selectedTasksList = useSelector(selectSelectedTasksList);                 // holds list of selected tasks
    const preBoard = useSelector(selectPreBoard);                                   // status of auto accept unplaned task switch
    const flightList = useSelector(selectFlightListIndexed)                         // list of flights from flight list
    const thirtyMinLayout = useSelector(selectThirtyMinLayout);
    const iata = userDb.details.authority;                                          // IATA code of logged in user
    const usedTheme = useSelector(selectUsedTheme);
    const removePrefixName = useSelector(selectRemovePaxNamePrefix)                 // If true, remove prefix like Mr,Ms from passenger name
    const selectedMultiplePAXTask = useSelector(selectSelectedMultiplePAXTask);   // selected task with multiple pax from the job list

    //#endregion

    //#region local states
    const [flightNumber, setFlightNumber] = useState("");                                                           // flight number of booking
    const [displayFlightNumber, setDisplayFlightNumber] = useState("");                                             // display flight number of booking
    const [departure, setDeparture] = useState("");                                                                 // departure airport of booking
    const [arrival, setArrival] = useState("");                                                                     // arrival airport of booking
    const [displayedAssistance, setDisplayedAssistance] = useState("WCHR");                                         // passenger assistance
    const [fullAssistance, setFullAssistance] = useState('')
    const [time, setTime] = useState("");                                                                           // time of flight departure / arrival
    const [flightType, setFlightType] = useState("");                                                               // type of flight (inbound or outbound)
    const [toggleAcceptModal, setToggleAcceptModal] = useState(false);                                              // shows task accept modal
    const [name, setName] = useState('')                                                                            // name of the passenger
    const [isClicked, setIsClicked] = useState(false);                                                              // check if the job card is clicked 

    const [noShow, setNoShow] = useState(false);                                                                    // job will be marked as no show if it is in the past by 10 minutes

    // gate + terminal information
    const [gateInformation, setGateInformation] = useState("")
    const [standInformation, setStandInformation] = useState("")
    const [jobHasNote, setJobHasNote] = useState(false)                                                                // show note icon if booking has a note.
    const [flightDoc, setFlightDoc] = useState(null);                                                               // holds the flight doc from airport schedule db matching booking flight number
    const [breakTaskPeriod, setBreakTaskPeriod] = useState("")                      // holds value of the break task period
    const [jobCardWidth, setJobCardWidth] = useState(0)                  // default job card with to be 30 min default sizing
    const [noPreboardAssistanceRequired, setNoPreboardAssistanceRequired] = useState(false)
    const [isRejected, setIsRejected] = useState(false)
    const [duplicateJob, setDuplicateJob] = useState(false)

    //#endregion

    //#region useEffects

    // set name if it is a departure booking
    useEffect(() => {
        let name = ""
        if (props.job && !props?.job?.combinedBooking && !props?.job?.combinedRampBooking) {

            if (props.job.passengerInformation) {
                if (props.job.passengerInformation.name) {
                    name = props.job.passengerInformation.name
                }
                else if (props.job.passengerInformation.title) {
                    name = props.job.passengerInformation.title;
                }
            }
            else if (props.job.name) {
                name = props.job.name;
            }
            else {
                // only set arrival name if it has been assigned
                if (props?.assignedTime == true) {

                    // arrival tasks should show name
                    if (props.job.passengerInformation) {
                        if (props.job.passengerInformation.name) {
                            name = props.job.passengerInformation.name;
                        }
                        else if (props.job.passengerInformation.title) {
                            name = props.job.passengerInformation.title;
                        }
                    }
                    else if (props.job.name) {
                        name = props.job.name;
                    }
                }
            }
        }
        if (removePrefixName && name) {
            setName(processName(name, true))
        } else {
            setName(processName(name))

        }
    }, [flightType, removePrefixName, props.job]);


    // find flight doc for this booking
    useEffect(() => {
        // check flight number
        if (flightNumber != "") {

            // check flight list is valid
            if (flightList && Object.keys(flightList) && Object.keys(flightList).length > 0) {

                // code switch if required
                let tempNumber = flightNumber;

                if (tempNumber?.includes('EZY')) {
                    // need to replace 'EZY' with 'U2'
                    tempNumber = tempNumber?.replace('EZY', 'U2');
                }

                // try find our flight
                let flightDocument = flightList[`${tempNumber}_${departure}_${arrival}`];

                if (flightDocument != undefined) {
                    // set flight doc
                    setFlightDoc(flightDocument)
                }
                else {
                    // no flight exists for this booking
                    setFlightDoc(null)
                }
            }
        }
    }, [flightNumber, flightList, departure, arrival]);

    // extract gate info from flight list
    useEffect(() => {
        // check if flight doc exists
        if (flightDoc != undefined) {
            // check for gate / stand info
            if (flightDoc.stand && flightDoc.stand !== "N/A") {
                setStandInformation(flightDoc.stand)
            }
            if (flightDoc.gate && flightDoc.gate !== "N/A") {
                setGateInformation(flightDoc.gate)
            }
        }
        else {
            // no flight exists for this booking
            setStandInformation("")
            setGateInformation("")
        }
    }, [flightDoc]);

    // handle if job has not been assigned (job is still in timeline jobs list not on timeline)
    useEffect(() => {
        // check if assigned time is null, then we know booking has not been assigned
        if (props?.job?.assignedTime == undefined || (props?.job?.flightConfirmationDetails != undefined && props?.job?.flightConfirmationDetails?.departureTime != undefined)) {
            // listed booking in job list - not assigned yet

            //#region flight number / dep / arr

            if (props?.job?.flightBooking && props?.job?.originalBookingDoc?.notes) setJobHasNote(true);
            else if (props?.job?.notes || props?.job?.taskNotes) setJobHasNote(true);
            else setJobHasNote(false);
            if (props.job.noPreboardAssistanceRequired) {
                setNoPreboardAssistanceRequired(props.job.noPreboardAssistanceRequired)
            }
            //Accept only id with pattern basic_ID-part{duplicatecount}
            if (/^.+-part\d+$/.test(props.job._id)) {
                setDuplicateJob(true);
            }

            let carrierCode = ""
            // do code switch
            if (props?.job?.isALTPickUp) {
                // set flight number + dep / arr airports
                if (props?.job?.flightInformation) {
                    setDisplayFlightNumber(props?.job?.flightInformation?.flightNumber?.replace('U2', 'EZY'));
                    setFlightNumber(props?.job?.flightInformation?.flightNumber);
                    setDeparture(props?.job?.flightInformation?.departureAirport);
                    setArrival(props?.job?.flightInformation?.arrivalAirport);

                } else {
                    carrierCode = props?.job?.flightConfirmationDetails?.carrierCode;
                    if (carrierCode == 'U2') { carrierCode = 'EZY' }

                    // set flight number + dep / arr airports
                    setDisplayFlightNumber(carrierCode + props?.job?.flightConfirmationDetails?.flightNumber);
                    setFlightNumber(props?.job?.flightConfirmationDetails?.carrierCode + props?.job?.flightConfirmationDetails?.flightNumber);

                    setDeparture(props?.job?.flightConfirmationDetails?.origin);
                    setArrival(props?.job?.flightConfirmationDetails?.destination);
                }
                if (props?.job?.taskNotes) setJobHasNote(true);
                else setJobHasNote(false);



            } else {
                carrierCode = props?.job?.flightConfirmationDetails?.carrierCode;
                if (carrierCode == 'U2') { carrierCode = 'EZY' }

                // set flight number + dep / arr airports
                setDisplayFlightNumber(carrierCode + props?.job?.flightConfirmationDetails?.flightNumber);
                setFlightNumber(props?.job?.flightConfirmationDetails?.carrierCode + props?.job?.flightConfirmationDetails?.flightNumber);
                setDeparture(props?.job?.flightConfirmationDetails?.origin);
                setArrival(props?.job?.flightConfirmationDetails?.destination);
            }


            //#endregion

            //#region flight time

            // try use flight record for time if it exists
            if (flightDoc && flightDoc?.operationInfo && flightDoc?.operationInfo.time) {
                if (props?.job?.combinedBooking) {
                    setTime(getTimeString((new Date(flightDoc?.operationInfo?.time)?.getTime() - 30 * 60 * 1000)))
                } else {
                    setTime(getTimeString(flightDoc?.operationInfo?.time));
                }
            }
            else {
                // use booking / task time instead
                if (userDb.details.authority === props?.job?.flightConfirmationDetails?.origin && props?.job?.flightConfirmationDetails?.departureTime) {
                    setTime(getTimeString(props?.job?.passengerDutTime ? props?.job?.passengerDutTime : props?.job?.flightConfirmationDetails?.departureTime));
                } else if (userDb.details.authority === props?.job?.flightConfirmationDetails?.destination && props?.job?.flightConfirmationDetails?.arrivalTime) {
                    setTime(getTimeString(props?.job?.flightConfirmationDetails?.arrivalTime));
                }
            }

            //#endregion



            //#region WHAT IS THIS CODE???
            // check if the job has a rejectedPax change it's status to Rejected
            if (Array.isFullArray(props?.job?.assistance) && props?.job?.assistance.length > 0) {

                let tempAssistance = [
                    ...new Set(
                        props?.job?.assistance
                            ?.map(item => (typeof item === "string" ? item?.match(/\b[A-Z]{4}\b/g) : null)) // Ensure item is a string
                            .flat()
                            .filter(Boolean)
                    )
                ];
                tempAssistance = tempAssistance?.filter((row) => PRMCodes.includes(row.trim()))


                // check if tempAssistance is an array and the length > 0
                if (Array.isFullArray(tempAssistance)) {
                    // check if the there is more than one assistance display only one with ...
                    if (tempAssistance?.length > 1) {
                        setDisplayedAssistance(tempAssistance.slice(0, 1)?.filter((row) => PRMCodes.includes(row.trim())).join(", ") + `...+${tempAssistance?.length - 1}`);
                    } else {
                        setDisplayedAssistance(tempAssistance?.filter((row) => PRMCodes.includes(row.trim())).join(", "));
                    }
                    setFullAssistance(tempAssistance.join())
                } else {
                    setDisplayedAssistance("WCHR");
                    setFullAssistance('WCHR')
                }

            }

            //#endregion

        }
    }, [props.job, flightDoc]);

    useEffect(() => {
        if (props?.job && !props?.job?.assignedTime) {
            const isRejectedArrJourney = props?.job?.originalBookingDoc?.paxRejection?.find((paxRejection) => paxRejection?.type === "arrivalJourney");
            const isRejectedDepJourney = props?.job?.paxRejection?.find((paxRejection) => paxRejection?.type === "preBoardJourney");
            const isRejectedArrPax = props?.job?.originalBookingDoc?.paxRejection?.find((paxRejection) => paxRejection?.type === "arrivalPax");
            const isRejectedDepPax = props?.job?.paxRejection?.find((paxRejection) => paxRejection?.type === "departurePax");
            if (isRejectedArrJourney || isRejectedDepJourney || isRejectedArrPax || isRejectedDepPax || props?.job?.rejectedPax || props?.job?.originalBookingDoc?.rejectedPax) {
                setIsRejected(true);
            }
        } else {
            setIsRejected(false);
        }
    }, [props.job]);

    // set job states if there is  assignedTime ( means it is in employee list / on timeline )
    useEffect(() => {
        // check if assigned time exists or if it is a break task
        if (props.job && props.job.assignedTime != undefined && props?.job?.isBreakTask !== true) {
            if (props?.job?.flightBooking && props?.job?.originalBookingDoc?.notes) setJobHasNote(true);
            else if (props?.job?.notes || props?.job?.taskNotes) setJobHasNote(true);
            else setJobHasNote(false);
            //#region setting flight number

            // check for flight number
            if (props.job.flightInformation != undefined && props.job.flightInformation.flightNumber != undefined) {
                let number = props?.job?.flightInformation?.flightNumber;

                // code switch 
                if (number?.includes('U2')) { number = number?.replace('U2', 'EZY') }

                setDisplayFlightNumber(number);
                setFlightNumber(props?.job?.flightInformation?.flightNumber);

            }

            //#endregion

            //#region setting departure / arrival airport
            setDeparture(props?.job?.flightInformation?.departureAirport);
            setArrival(props.job?.flightInformation?.arrivalAirport);
            //#endregion

            //#region check for flight time
            if (flightDoc && flightDoc?.operationInfo && flightDoc?.operationInfo.time) {
                if (props?.job?.combinedBooking) {
                    setTime(getTimeString((new Date(flightDoc?.operationInfo?.time)?.getTime() - 30 * 60 * 1000)))
                } else {
                    setTime(getTimeString(flightDoc?.operationInfo?.time));
                }
            } else if (props.job.flightInformation?.flightTime != undefined) {
                setTime(getTimeString(props?.job?.passengerDutTime ? props?.job?.passengerDutTime : props.job.flightInformation.flightTime));
            }
            //#endregion

            //#region check for assistance
            if (Array.isFullArray(props?.job?.passengerInformation?.assistance)) {

                // trim whitespace from assistance codes
                let tempAssistance = [...new Set(props?.job?.passengerInformation?.assistance.map(item => item?.match(/\b[A-Z]{4}\b/g))?.flat()?.filter(Boolean))]
                tempAssistance = tempAssistance?.filter((row) => PRMCodes.includes(row.trim()))

                // check if assistances exist
                if (Array.isFullArray(tempAssistance)) {
                    // check if the there is more than one assistance display only one with ...
                    if (tempAssistance?.length > 1) {
                        // add ... instead of listing each assistance item
                        setDisplayedAssistance(tempAssistance.slice(0, 1)?.filter((row) => PRMCodes.includes(row.trim())).join(", ") + `...+${tempAssistance?.length - 1}`);
                    }
                    else {
                        // display assistance
                        setDisplayedAssistance(tempAssistance?.filter((row) => PRMCodes.includes(row.trim())).join(", "));
                    }
                    setFullAssistance(tempAssistance.join())
                }
                else {
                    // no assistance found
                    setDisplayedAssistance("WCHR");
                    setFullAssistance('WCHR');
                }
            }
            //#endregion

            //#region set job status

            //#endregion

        }
    }, [props.job, flightDoc]);

    // check if job needs to be marked as no show
    useEffect(() => {

        // check if job is an alt pickup task
        if (props?.job?.isALTPickUp) {
            // set as show
            setNoShow(false);
            return;
        }

        if (props.job?.isBreakTask) {

            setTime(getTimeString(`${props.job?.breakTaskInformation?.breakTaskDate}T${props.job?.assignedTime}:00`));
            setDisplayedAssistance("");
            setBreakTaskPeriod(props.job?.breakTaskInformation?.breakTaskPeriod)
        }

        let flightType = 'arrival';
        if (props?.job?.flightConfirmationDetails?.origin == iata) {
            flightType = 'departure';
        }

        // if job is departure
        if (flightType == 'departure') {
            // check if job is checked in
            if (props?.job?.paxPositionInfo?.activity == "Checked-in" || props?.job?.assignedTime) {
                setNoShow(false);
            } else {
                setNoShow(true);
            }
        }
        else {
            // job is arrival
            // check if job has been assigned
            if (props?.job?.assignedTime) {
                setNoShow(false);
            }
            // check if job is in the past
            else if (props?.job?.flightConfirmationDetails?.arrivalTime) {
                let flightTime = new Date(props?.job?.flightConfirmationDetails?.arrivalTime);
                let currentTime = new Date();

                // check if the flight time is in the past by more than 10 minutes
                if (currentTime > flightTime) {
                    const timeDiff = Math.abs(currentTime - flightTime);
                    const diffMinutes = Math.floor((timeDiff / 1000) / 60);
                    if (diffMinutes > 10) { setNoShow(true); }
                    else { setNoShow(false) }
                }
            }
        }

    }, [props?.job]);

    // check job card with is correct
    useEffect(() => {
        if (props.job && thirtyMinLayout) {
            // check if job is started
            if (!props.job.active && !props.job.completed) {

                // default sizing to 30 minutes
                setJobCardWidth(JobCard30MinLayoutWidth)
            }
            // check if job is started
            if (props.job.active && !props.job.completed) {

                // create width by calculating difference in minutes between assigned time and current time
                const assignedTime = props.job.assignedTime;

                let timeDifference = minutesSince(assignedTime);

                // set the job card width to be the time difference in minutes * 30
                let newJobWidth = timeDifference * minute30MinLayoutWidth;

                setJobCardWidth(newJobWidth && newJobWidth < 25 ? 25 : newJobWidth);

            }
            // check if job completed
            if (props.job.completed) {

                let completeTime = props.job.completeTime

                let completeDate = new Date(completeTime.seconds * 1000 + Math.floor(completeTime.nanoseconds / 1e6));

                // format complete time properly
                const formattedTime = completeDate.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false, // Use 24-hour format; change to true for 12-hour format
                });

                let assignedTime = props.job.assignedTime;

                // find time taken to complete job
                let timeDifference = calculateMinuteDifference(assignedTime, formattedTime);

                let newJobWidth = timeDifference * minute30MinLayoutWidth;

                setJobCardWidth(newJobWidth && newJobWidth < 25 ? 25 : newJobWidth);

            }
        }
    }, [props.job]);

    //WHAT IS THIS CODE???
    useEffect(() => {
        // check if it is an alternative pickup task set the gate and stand from the passed outboundFlights if there is outboundFlights
        if (props?.job?.isALTPickUp) {
            if (Array.isFullArray(props?.outboundFlights)) {
                let tempFlightInfo = props?.outboundFlights?.find((flight) => flight?.flightNumber === displayFlightNumber);
                if (tempFlightInfo?.gate && tempFlightInfo?.gate !== "N/A") setGateInformation(tempFlightInfo?.gate);
                if (tempFlightInfo?.stand && tempFlightInfo?.STAND !== "N/A") setStandInformation(tempFlightInfo?.stand);
            }
        }

    }, [props.job, displayFlightNumber])

    // check if arrival or departure for logged in user
    useEffect(() => {
        if (arrival == userDb.details.authority) {
            setFlightType("arrival");
        } else {
            setFlightType("departure");
        }
    }, [arrival, departure]);

    // check if job has been clicked or not
    useEffect(() => {
        const selectedTasksIds = Array.isFullArray(selectedTasksList)
            ? selectedTasksList?.filter((element) => element?._id == props?.job["_id"])
            : [];
        if (selectedTasksIds.length) {
            setIsClicked(true);
        }
        else {
            setIsClicked(false);
        }
    }, [selectedTasksList, props?.job]);
    //#endregion

    //#region functions



    // truncate name if it is too long
    function truncateName(name) {
        if (name.length > 10) {
            return name.substring(0, 10) + '...';
        }
        return name;
    }
    // convert time string into hour + minutes string
    const getTimeString = (dateString) => {
        const dateObj = new Date(dateString);
        const options = { hour: "2-digit", minute: "2-digit", hour12: false };
        const formattedTime = dateObj.toLocaleString("en-US", options);
        return formattedTime;
    };

    // handle option click
    const handleLeftClick = (e) => {
        // e.stopPropagation();
        //console.log('handle left click called...', props?.job)
        // check if the job is break task and completed don't open context menu
        if (props?.job?.completed && props?.job?.isBreakTask) return;
        e.preventDefault();

        // open context menu
        dispatch(setClicked(true));
        dispatch(setPoints({ x: e.pageX, y: e.pageY }));
        dispatch(setClickedJob(props.job));
        dispatch(resetFlightContext());
    };

    // handle right click
    const handleJobClick = (e) => {
        // if (props?.disabled) e.stopPropagation();
        // check if click was double click
        if (e.detail === 2) { return; /* do not do anything if double click */ }

        // check if the pax is rejected 
        if (isRejected) return;
        // props?.disabled == true refers that the job is assigned to an employee
        if (props?.disabled) {
            // props.job?.pending == true refers that the job is in pending status and waits for approve
            if (props.job?.pending) setToggleAcceptModal(true);
            // else handleLeftClick(e);
            return;
        }

        // check if there is a list of job selected and viewed to be assigned
        if (props?.toConfirm == true) {
            if (props?.forMultiplePaxAssign == true) {
                let tempSelectedMultiplePAXTask = Array.isFullArray(selectedMultiplePAXTask) ? [...selectedMultiplePAXTask] : [];

                if (props?.job?.combinedBooking) {
                    dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for combined bookings to assign to the same employee at the same time"));
                    return;
                } else if (props?.job?.combinedRampBooking) {
                    dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for combined ramp bookings to assign to the same employee at the same time"));
                    return;
                } else if (props?.job?.isALTPickUp) {
                    dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for ALT pickup tasks to assign to the same employee at the same time"));
                    return;
                } else if (props?.job?.isBreakTask) {
                    dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for break tasks to assign to the same employee at the same time"));
                    return;
                }

                if (tempSelectedMultiplePAXTask?.map((item) => item?._id)?.includes(props?.job?._id)) {
                    tempSelectedMultiplePAXTask = tempSelectedMultiplePAXTask?.filter((item) => item?._id != props?.job?._id);
                }
                else {
                    const firstSelectedTask = Array.isFullArray(tempSelectedMultiplePAXTask) ? tempSelectedMultiplePAXTask?.[0] : null;

                    const selectedFlightNumber = firstSelectedTask?.flightConfirmationDetails?.carrierCode + firstSelectedTask?.flightConfirmationDetails?.flightNumber;
                    console.log(firstSelectedTask, selectedFlightNumber, props?.job?.flightConfirmationDetails?.carrierCode + props?.job?.flightConfirmationDetails?.flightNumber, "selectedFlightNumber");
                    if (selectedFlightNumber && selectedFlightNumber !== props?.job?.flightConfirmationDetails?.carrierCode + props?.job?.flightConfirmationDetails?.flightNumber) {
                        dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for different flights to assign to the same employee at the same time"));
                        return;
                    } else {
                        tempSelectedMultiplePAXTask.push(props?.job);
                    }
                }
                dispatch(setSelectedMultiplePAXTask(tempSelectedMultiplePAXTask));
                dispatch(setSelectedMultiplePAXTaskError(null));
            }
            else {
                // check if selected task is null and if its the same task
                if (selectedTask != null && selectedTask?._id == props?.job?._id) {
                    // selected task is current job, unselect it
                    if (props?.clicked?.includes(props?.job["_id"])) {
                        props?.setClicked(null);
                        dispatch(setSelectedTask(null));
                    }
                } else {
                    props?.setClicked(props?.job["_id"]);
                    dispatch(setSelectedTask(props.job));
                }
            }
        }

        else {

            // set selected job lists by click on job cards
            // selected taks id
            let selectedTasksIds = Array.isFullArray(selectedTasksList)
                ? selectedTasksList?.map((element) => element?._id)
                : [];
            // check if the selected tasks list include the selected task or not
            if (selectedTasksIds?.includes(props?.job["_id"])) {
                // remove the clicked task from selected tasks list
                dispatch(
                    setSelectedTaskList(
                        selectedTasksList?.filter((item) => item?._id != props?.job["_id"])
                    )
                );
                props?.setClicked(
                    selectedTasksIds?.filter((item) => item != props?.job["_id"])
                );
            }
            else {
                dispatch(setSelectedTaskList([...selectedTasksList, props.job]));
                props?.setClicked([...selectedTasksIds, props?.job["_id"]]);
            }
        }
    };

    function minutesSince(timeString) {
        const [hours, minutes] = timeString.split(':').map(Number); // Parse the input time
        const inputMinutes = hours * 60 + minutes; // Convert input time to total minutes since midnight

        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes(); // Convert current time to total minutes since midnight

        return currentMinutes - inputMinutes; // Calculate the difference in minutes
    }

    function calculateMinuteDifference(time1, time2) {
        // Helper function to convert hh:mm to total minutes
        const timeToMinutes = (time) => {
            const [hours, minutes] = time.split(":").map(Number);
            return hours * 60 + minutes;
        };

        // Convert both times to total minutes
        const minutes1 = timeToMinutes(time1);
        const minutes2 = timeToMinutes(time2);

        // Calculate the absolute difference
        const difference = Math.abs(minutes2 - minutes1);

        return difference;
    }

    // set the card top border color by the task status
    const handleCardTopColor = () => {

        if (props.job.bookingType === "TRANSIT") return TaskColors.transitBookings
        // check if alt pickup task
        if (props?.job?.isALTPickUp) { return TaskColors.ALTPickUps }

        // check if break task
        if (props?.job?.isBreakTask) return TaskColors.breakTasks

        // check if transit booking
        if (props?.job?.isTransitBooking == true
            && props?.job?.pending !== true
            && props?.job?.active !== true
            && props?.job?.cancelled !== true
            && props?.job?.completed !== true) {
            return TaskColors.transitBookings
        }

        // check if pending
        if (props?.job?.pending == true) {
            return TaskColors.pendingTasks

        };

        // check if active
        if (props?.job?.active == true) return TaskColors.activeTasks;

        // check if cancelled
        if (props.job.cancelled == true) return TaskColors.rejectedTasks;

        // check if completed
        if (props?.job?.completed == true) return TaskColors.completedTasks;

        // check if stopped
        if (props?.job?.stopped == true) return TaskColors.rejectedTasks;

        // check if acknowledged
        if (props?.job?.acknowledged == true || props?.job?.isAtLocation == true) return TaskColors.acknowledgedTasks;

        // check if grouped booking
        if (props?.job?.combinedBooking) {
            return TaskColors.combinedBooking;
        }

        if (props?.job?.combinedRampBooking) {
            return TaskColors.combinedRampBooking;
        }
        // check for arrival
        if (flightType == "arrival") {
            return TaskColors.arrivals
        }

        // check for departure
        if (flightType == "departure") {
            return TaskColors.departures
        }

        return "lightgrey";
    };

    const getCardBottomColor = () => {
        if (props?.job?.isALTPickUp) return 'purple';
        if (flightType === "departure") return props?.job?.combinedBooking ? TaskColors.combinedBooking : Colors.PRM_LIGHT_BLUE
        else if (flightType === "arrival") return props?.job?.combinedRampBooking ? TaskColors.combinedRampBooking : Colors.LIGHT_GREEN
        else return 'lightgrey';
    }

    const getCardClickedColor = () => {
        if (props?.job?.isALTPickUp) return '#f5b5d8';
        if (flightType === "departure") return '#C7E2FD'
        else if (flightType === "arrival") return '#BDF4B6'
        else return 'lightgrey';
    }

    // handle closing modal
    const closeModal = () => {
        setToggleAcceptModal(false);
    };

    //#endregion

    //#region card styling
    const cardStyle = {
        ...mainContainer,
        borderColor: handleCardTopColor(),
        borderTopColor: handleCardTopColor(),       // borderLeftColor:,
        backgroundColor: props?.jobConfirmation ? usedTheme.BACKGROUND_COLOR : isClicked ? getCardClickedColor() : usedTheme.BACKGROUND_COLOR,
        width: thirtyMinLayout ?
            props?.disabled ? jobCardWidth : JobCardWidth
            : props?.disabled ? EmployeeJobCardWidth : JobCardWidth,
        position: props.position ? props.position : 'relative',
        overflowX: 'hidden',
        //backgroundColor: usedTheme.BACKGROUND_COLOR,
        borderColor: usedTheme.BORDER_COLOR,
        color: usedTheme.TEXT_COLOR,
        maxHeight: "73px",
    };

    //#endregion

    //#region error / break card
    // error checking
    if (props.job == undefined || props.job["_id"] == undefined) {
        return <p></p>;
    }
    // break task
    if (props.job.isBreakTask) {
        return <BreakTaskCard
            job={props.job}
            clicked={props.clicked}
            setClicked={props.setClicked}
            disabled={props.disabled}
            handleLeftClick={handleLeftClick}
            handleJobClick={handleJobClick}
            setToggleAcceptModal={setToggleAcceptModal}
            toggleAcceptModal={toggleAcceptModal}
            closeModal={closeModal}
            cardStyle={cardStyle}
        />
    }
    //#endregion

    return (
        <div>
            {/* job card */}
            <div
                onDoubleClick={(e) => { console.log('handle double click...'), handleLeftClick(e) }}
                onClick={(e) => { console.log('handle click....'), handleJobClick(e) }}
                style={{
                    display: "flex",
                    justifyContent: "center",
                    //opacity: noShow ? 0.5 : 1,
                    // width: thirtyMinLayout && props?.disabled ? jobCardWidth : EmployeeJobCardWidth,
                    zIndex: props?.job?.paxPositionInfo?.activity == "Checked-in" ? 2 : 1,
                    position: props.position ? props.position : 'relative',

                }}>

                <div style={{
                    width: thirtyMinLayout && props?.disabled == true ? jobCardWidth : EmployeeJobCardWidth + 10, display: "flex", justifyContent: "center"
                }}>
                    <div onContextMenu={(e) => { handleLeftClick(e) }} style={cardStyle}>

                        {/* top half of card */}
                        <div key={fullAssistance || "default"} style={{ display: "flex", flex: 1, justifyContent: "space-between" }}>

                            {/* assistance */}
                            <div className="tippybox" style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', marginLeft: Spacing.SCALE_2 }} {...(fullAssistance != '' ? { 'data-tippy-content': fullAssistance } : {})} >
                                <p style={{ margin: "0px 0", textAlign: "center", fontSize: 10, fontWeight: 'bold' }} >{props?.job?.isBreakTask ? breakTaskPeriod : displayedAssistance}</p>
                            </div>

                            {/* preboard assistance */}
                            {noPreboardAssistanceRequired && <div className="tippybox" style={{ flex: 1, display: 'flex', alignItems: 'flex-start' }} {...(noPreboardAssistanceRequired ? { 'data-tippy-content': "No Preboard Assistance Required" } : {})} >
                                <p style={{ margin: "0px 0", textAlign: "left", fontSize: 10, fontWeight: 'bold' }} >PAR</p>
                            </div>}
                            {duplicateJob && <div className="tippybox" style={{ flex: 1, display: 'flex', alignItems: 'flex-start' }} {...(duplicateJob ? { 'data-tippy-content': "Duplicated job" } : {})} >
                                <p style={{ margin: "0px 0", textAlign: "left", fontSize: 14, fontWeight: 'bold' }} ><GoDuplicate /></p>
                            </div>}

                            {/* note icon */}
                            {jobHasNote && <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }} {...(jobHasNote ? { 'data-tippy-content': handleNotesText(props?.job, "tooltip") } : {})}>
                                <PiNotepadFill color={Colors.GRAY_DARK} size={14} />
                            </div>}

                            {/* flight time */}
                            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                                <p style={{ margin: "0px 0", textAlign: "center", fontSize: 10, marginRight: Spacing.SCALE_2 }}>
                                    {time}
                                </p>
                            </div>

                        </div>

                        {/* horizontal line break */}
                        <hr style={{ margin: "0px" }} />

                        {/* middle half of card */}
                        <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', padding: Spacing.SCALE_2, maxHeight: "20px", }}>

                            {/* passenger name */}
                            <div style={{ flex: 1.5, overflowX: "hidden", }}>
                                {/* combinedBooking(departures combined booking),combinedRampBooking(arrivals combined ramp booking), selfAllocatedTask with passengers array, buggyTask(arrival task set with multiple passengers for buggy staff)  */}
                                {(props?.job?.combinedBooking || props?.job?.combinedRampBooking || (props?.job?._id?.includes("-selfAllocatedTask-") && props?.job?.passengers?.length > 1) || (props?.job?.buggyTask === true && props?.job?.flightBooking === true) || (props?.job?.groupedBooking == true && props?.job?.passengers?.length > 1))
                                    ? <div {...((props?.job?.combinedBooking || props?.job?.combinedRampBooking) && props?.job?.passengers?.length ? { 'data-tippy-content': `Grouped ${props?.job?.combinedBooking ? "Boarding Departures" : "Ramp Arrivals"} with ${props?.job?.passengers?.length} passengers` } : {})}>
                                        <GroupedPassengerIcon
                                            groupedPassengers={props?.job?.passengers}
                                            flightType={flightType}
                                            job={props?.job}
                                        />
                                    </div>
                                    : <p style={{ margin: "0px 0", fontSize: 10, fontWeight: "500" }} {...(name !== "" ? { 'data-tippy-content': name } : {})}>
                                        {truncateName(name)}

                                    </p>}
                            </div>

                            {/* status information */}
                            <div style={{ display: "flex", flex: 1, alignItems: "flex-end", justifyContent: 'flex-end' }}>


                                {/* show pickup location for alt pickup if it is not assigned */}
                                {props?.job?.isALTPickUp && props?.job?.assignedTime == undefined && !isRejected &&
                                    <p style={{ margin: "0px 0", textAlign: "flex-end", fontSize: 10, fontWeight: 'bold', color: 'black' }} {...(props?.job?.pickUpLocation?.name !== "" ? { 'data-tippy-content': `Pick Up Location: ${props?.job?.pickUpLocation?.name}` } : {})}>{truncateName(props?.job?.pickUpLocation?.name)}</p>
                                }

                                {/* status */}
                                <JobStatus job={props?.job} />

                            </div>
                        </div>

                        {/* bottom half of card */}
                        <div style={{ backgroundColor: getCardBottomColor(), margin: 0, padding: 0 }}>
                            <div style={{ display: "flex", flex: 1, justifyContent: "space-between", padding: Spacing.SCALE_2 }}>

                                {/* flight number */}
                                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                                    <p style={{ margin: "0px 0", textAlign: "center", fontSize: 11, fontWeight: '600', color: 'white' }} data-tippy-content="Flight Number">
                                        {displayFlightNumber}
                                    </p>
                                </div>

                                {/* iata code */}
                                <div style={{ display: "flex", flexDirection: "row", justifyContent: "start" }}>
                                    <p style={{ margin: "0px 0", textAlign: "center", fontSize: 11, fontWeight: '600', color: 'white' }}>{flightType === 'departure' ? arrival : flightType === 'arrival' ? departure : ''}</p>
                                    {props?.job?.bookingType === "TRANSIT" && <span {...({ 'data-tippy-content': "Transit pax" })}><MdConnectingAirports size={16} /></span>}
                                </div>

                                {/* gate + stand */}
                                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>

                                    {/* gate */}
                                    {gateInformation != '' && <p style={{ margin: "0px 0", textAlign: "center", fontSize: 11, color: 'white' }}>{'G' + gateInformation}</p>}

                                    {/* stand */}
                                    {standInformation != '' && <p style={{ margin: "0px 0", textAlign: "center", fontSize: 11, color: 'white' }}>{'S' + standInformation}</p>}

                                </div>

                            </div>

                        </div>

                    </div>

                </div>


            </div>


            <TaskAccept task={props?.job} closeModal={closeModal} toggleAcceptModal={toggleAcceptModal} />

        </div>
    );
};

export default JobCard;
